import { useQuery } from "@tanstack/react-query";
import ky, { HTTPError } from "ky";

import type {
	LoadTrackingEvent,
	LoadTrackingStop,
} from "~/shared/schemas/load-tracking-schema.ts";
import { loadTrackingResponseSchema } from "~/shared/schemas/load-tracking-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { environmentConfig } from "~/shared/utils/environment-config.ts";
import { extractApiError } from "~/shared/utils/extract-api-error.ts";

type TrackingUpdate = {
	dateTime: string;
	sortDate: string;
	status: string;
	location: string;
	distance: string;
	latitude: number | null;
	longitude: number | null;
};

type TrackingStop = {
	sortOrder: number | null;
	stopType: string;
	location: string;
	locationWithZip: string;
	latitude: number | null;
	longitude: number | null;
	stopDate: string | null;
};

type UseGetPublicTrackingResult = {
	trackingUpdates: TrackingUpdate[];
	stops: TrackingStop[];
	mode: string | null;
	status: string | null;
	message: string | null;
	proNumber: string | null;
	isLoading: boolean;
	isFetching: boolean;
	error: Error | null | undefined;
	errorMessage: string | null;
	pickedUpDate: string | null;
	deliveredDate: string | null;
};

function formatDateTime(value: string): string {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return date.toLocaleString("en-US", {
		timeZone: "America/New_York",
		month: "numeric",
		day: "numeric",
		year: "2-digit",
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	});
}

function normalizeEvent(event: LoadTrackingEvent): TrackingUpdate {
	const city = event.city?.trim();
	const state = event.state?.trim();
	const location = city && state ? `${city}, ${state}` : (city ?? state ?? "-");

	return {
		dateTime: formatDateTime(event.locationDateTime),
		sortDate: event.locationDateTime,
		status: event.status?.trim() || "-",
		location,
		distance: event.distance == null ? "-" : String(event.distance),
		latitude: event.latitude ?? null,
		longitude: event.longitude ?? null,
	};
}

function normalizeStop(stop: LoadTrackingStop): TrackingStop {
	const city = stop.city?.trim();
	const state = stop.state?.trim();
	const zip = stop.zipCode?.trim();
	const location = city && state ? `${city}, ${state}` : (city ?? state ?? "-");

	let locationWithZip = city ?? state ?? "-";
	if (city && state && zip) {
		locationWithZip = `${city}, ${state} ${zip}`;
	} else if (city && state) {
		locationWithZip = `${city}, ${state}`;
	}

	return {
		sortOrder: stop.sortOrder ?? null,
		stopType: stop.stopType,
		location,
		locationWithZip,
		latitude: stop.latitude ?? null,
		longitude: stop.longitude ?? null,
		stopDate: stop.stopDate ?? null,
	};
}

const publicApi = ky.create({
	prefixUrl: environmentConfig.services.ltlQuoting.url,
	headers: {
		Accept: "application/json",
		"Content-Type": "application/json",
	},
	timeout: false,
	retry: 0,
});

type TrackingData = {
	trackingUpdates: TrackingUpdate[];
	stops: TrackingStop[];
	mode: string | null;
	status: string | null;
	message: string | null;
	proNumber: string | null;
	pickedUpDate: string | null;
	deliveredDate: string | null;
};

const fetchPublicTracking = async (
	poNumber: number,
	signal?: AbortSignal,
): Promise<TrackingData> => {
	const url = apiRoutes.GETTRACKINGPUBLIC.replace(
		":poNumber",
		poNumber.toString(),
	);

	try {
		const json = await publicApi.get(url, { signal }).json();
		const result = loadTrackingResponseSchema.safeParse(json);

		if (!result.success) {
			throw new Error("Failed to validate tracking response");
		}

		return {
			trackingUpdates: result.data.trackingEvents.map((event) =>
				normalizeEvent(event),
			),
			stops: result.data.stops.map((stop) => normalizeStop(stop)),
			mode: result.data.mode ?? null,
			status: result.data.status ?? null,
			message: result.data.message ?? null,
			proNumber: result.data.proNumber ?? null,
			pickedUpDate: result.data.pickedUpDate ?? null,
			deliveredDate: result.data.deliveredDate ?? null,
		};
	} catch (error) {
		if (error instanceof HTTPError) {
			const apiError = await extractApiError(error);
			throw new Error(apiError?.error ?? "An unexpected error occurred.");
		}
		throw error;
	}
};

export function useGetPublicTracking(
	poNumber: number | null | undefined,
): UseGetPublicTrackingResult {
	const { data, isLoading, isFetching, error } = useQuery({
		queryKey: ["publicTracking", poNumber],
		queryFn: ({ signal }) => fetchPublicTracking(Number(poNumber), signal),
		enabled: poNumber !== null && poNumber !== undefined,
		gcTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	return {
		trackingUpdates: data?.trackingUpdates ?? [],
		stops: data?.stops ?? [],
		mode: data?.mode ?? null,
		status: data?.status ?? null,
		message: data?.message ?? null,
		proNumber: data?.proNumber ?? null,
		pickedUpDate: data?.pickedUpDate ?? null,
		deliveredDate: data?.deliveredDate ?? null,
		isLoading,
		isFetching,
		error,
		errorMessage: error?.message ?? null,
	};
}
